import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Typography from '@mui/material/Typography';
import { useLocation, useNavigate } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { Button, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { verifyBackupCode, select2FA } from '../../store/twoFAlice';
import JwtService from '../../auth/services/jwtService';
import { showMessage } from "app/store/fuse/messageSlice";

function TwoFaBackupCodePage() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { loading } = useSelector(select2FA);
    const [code, setCode] = useState('');

    const location = useLocation();
    const email = location?.state?.email;
    const password = location?.state?.password;

    const handleVerify = () => {
        if (code.trim() !== '') {
            dispatch(verifyBackupCode({ email, password, code: code.trim() })).unwrap().then((res) => {
                if (!res?.errors) {
                    JwtService.twoFaLogin(res);
                }
            }).catch((_errors) => {
                if (Array.isArray(_errors)) {
                    _errors.forEach((error) => {
                        dispatch(showMessage({ message: error?.message || "Invalid backup code", variant: "error" }));
                    });
                } else {
                    dispatch(showMessage({ message: _errors || "Verification Failed", variant: "error" }));
                }
            });
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleVerify();
        }
    };

    if (!email) {
        return (
            <div className="flex flex-col items-center flex-auto min-w-0 sm:justify-center">
                <Paper
                    sx={{
                        width: '600px',
                        minHeight: '100%',
                        px: { xs: 2, sm: 4 },
                        py: { xs: 4, sm: 6 },
                        borderRadius: { xs: 0, sm: 2 },
                        boxShadow: { xs: 'none', sm: 3 },
                        position: 'relative'
                    }}
                >
                    <IconButton
                        onClick={() => navigate('/sign-in')}
                        sx={{
                            position: 'absolute',
                            top: 16,
                            left: 16,
                            zIndex: 1
                        }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', mx: 'auto' }}>
                        <img className="w-60" src="assets/images/logo/logo.svg" alt="logo" />
                        <Typography sx={{ mt: 2, textAlign: 'center' }}>
                            Unauthenticated Access
                        </Typography>
                    </Box>
                </Paper>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center flex-auto min-w-0 sm:justify-center">
            <Paper className="relative w-full min-h-full px-16 py-32 sm:w-auto sm:min-h-auto rounded-0 sm:p-48 sm:rounded-2xl sm:shadow">
                <IconButton
                    onClick={() => navigate(-1)}
                    sx={{
                        position: 'absolute',
                        top: 16,
                        left: 16,
                        zIndex: 1
                    }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <div className="flex flex-col items-center justify-center w-full mx-auto max-w-320 sm:w-320 sm:mx-0">
                    <img className="w-60" src="assets/images/logo/logo.svg" alt="logo" />

                    <Typography className="mt-16 text-lg font-medium text-center">
                        Verify Authentication
                    </Typography>

                    <Typography className="mt-8 text-center text-gray-500">
                        Enter one of your 6-digit backup codes.
                    </Typography>

                    <Box className="flex flex-col w-full max-w-xs mx-auto mt-24">
                        <TextField
                            label="Backup Code"
                            variant="outlined"
                            fullWidth
                            type="number"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={loading}
                            inputProps={{
                                style: { textAlign: 'center', letterSpacing: '0.2rem', fontSize: '1.25rem' }
                            }}
                        />

                        <Button
                            className="py-12 mt-24"
                            variant="contained"
                            color="secondary"
                            onClick={handleVerify}
                            disabled={loading || code.trim() === ''}
                        >
                            {loading ? 'Verifying...' : 'Verify'}
                        </Button>
                    </Box>
                </div>
            </Paper>
        </div>
    );
}

export default TwoFaBackupCodePage;
